/**
 * Deleting a studio (`deleteStudio`; wave 7b-tail C): it cannot be undone, so
 * the studio's name is typed first. The API has no in-use guard — productions
 * made from the studio run from their own snapshot of it (D22) — so the dialog
 * names the ones not ended yet, and says they keep running, before it asks.
 * On a site with a main/backup pair the peer keeps its copy until it is
 * deleted there too (no tombstone; docs/redundancy.md's known gaps).
 */
import { useState } from 'react';
import { Button } from '../ui/button.js';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog.js';
import { Input } from '../ui/input.js';
import { Label } from '../ui/label.js';

export type DeleteStudioDialogProps = {
  readonly open: boolean;
  readonly studioName: string;
  /** Productions made from it that have not ended: `Rundown: Evening news`, … */
  readonly inUse: readonly { readonly productionId: string; readonly label: string }[];
  readonly pending: boolean;
  readonly error: string | null;
  readonly onConfirm: () => void;
  readonly onOpenChange: (open: boolean) => void;
};

function Body({ studioName, inUse, pending, error, onConfirm, onOpenChange }: Omit<DeleteStudioDialogProps, 'open'>) {
  const [typed, setTyped] = useState('');
  const ready = typed.trim() === studioName.trim();
  return (
    <>
      <DialogHeader>
        <DialogTitle>Delete studio {studioName}?</DialogTitle>
        <DialogDescription>Its devices, mappings and settings are deleted for good. New productions cannot be made from it.</DialogDescription>
      </DialogHeader>
      {inUse.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="studio-unused">
          No production made from it is running.
        </p>
      ) : (
        <div className="grid gap-1 rounded-md border border-warning p-3 text-sm" data-testid="studio-in-use">
          <strong>
            {String(inUse.length)} production{inUse.length === 1 ? '' : 's'} made from it {inUse.length === 1 ? 'has' : 'have'} not ended
          </strong>
          <ul className="list-disc pl-5">
            {inUse.map((p) => (
              <li key={p.productionId}>{p.label}</li>
            ))}
          </ul>
          <span className="text-muted-foreground">They keep running from their own copy of the studio; only new productions are affected.</span>
        </div>
      )}
      <div className="grid gap-2">
        <Label htmlFor="delete-studio-confirm">
          Type <span className="font-mono">{studioName}</span> to confirm
        </Label>
        <Input id="delete-studio-confirm" value={typed} autoComplete="off" onChange={(e) => setTyped(e.target.value)} data-testid="delete-studio-confirm" />
      </div>
      {error === null ? null : (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
          Cancel
        </Button>
        <Button variant="destructive" disabled={!ready || pending} onClick={onConfirm} data-testid="delete-studio-go">
          {pending ? 'Deleting…' : 'Delete studio'}
        </Button>
      </DialogFooter>
    </>
  );
}

export function DeleteStudioDialog(props: DeleteStudioDialogProps) {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent>{props.open ? <Body {...props} /> : null}</DialogContent>
    </Dialog>
  );
}
